import { useTranslation } from 'react-i18next';

import FormDialog from '@/components/atoms/FormDialog/FormDialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { SPEECH_PROVIDER_ICONS, SPEECH_PROVIDER_NAMES } from './helper';
import { useSpeechModelForm } from '../../hooks/useSpeechModelForm';
import { ELEVENLABS_REGIONS, SpeechModel } from '../../types';

interface SpeechModelModalProps {
  speechModel?: SpeechModel;
  onClose: () => void;
}

export const SpeechModelModal = ({ speechModel, onClose }: SpeechModelModalProps) => {
  const { t } = useTranslation();
  const { form, onSubmit, isEdit, providerOptions, showRegion } = useSpeechModelForm(
    speechModel,
    onClose
  );
  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = form;

  return (
    <FormDialog
      open
      onOpenChange={onClose}
      title={
        isEdit
          ? t('developer_tools.speech_models.edit_title')
          : t('developer_tools.speech_models.create_title')
      }
      submitLabel={isEdit ? t('common.save') : t('common.create')}
      onSubmit={handleSubmit(onSubmit)}
      submitDisabled={!isDirty || isSubmitting}
      isLoading={isSubmitting}
      submitOnEnter
    >
      <Form {...form}>
        <div className="space-y-4">
          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('common.name')}</FormLabel>
                <FormControl>
                  <Input {...field} autoFocus autoComplete="off" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="provider"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('developer_tools.language_models_provider')}</FormLabel>
                <Select value={field.value} onValueChange={field.onChange} disabled={isEdit}>
                  <FormControl>
                    <SelectTrigger className="w-full" onBlur={field.onBlur}>
                      <SelectValue placeholder={t('common.select_dots')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {speechModel && (
                      <SelectItem value={speechModel.provider}>
                        {SPEECH_PROVIDER_NAMES[speechModel.provider]}
                      </SelectItem>
                    )}
                    {!isEdit &&
                      providerOptions.map((option) => (
                        <SelectItem
                          key={option.provider}
                          value={option.provider}
                          disabled={option.disabled}
                        >
                          <span className="flex items-center gap-2">
                            {SPEECH_PROVIDER_ICONS[option.provider]}
                            {SPEECH_PROVIDER_NAMES[option.provider] ?? option.provider}
                          </span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="api_key"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('developer_tools.sidebar_api_key')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="password"
                    autoComplete="new-password"
                    placeholder={speechModel?.config.api_key ?? t('developer_tools.type_api_key')}
                  />
                </FormControl>
                {isEdit && (
                  <FormDescription>
                    {t('developer_tools.speech_models.keep_api_key')}
                  </FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          {showRegion && (
            <FormField
              control={control}
              name="region"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('developer_tools.speech_models.region')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full" onBlur={field.onBlur}>
                        <SelectValue placeholder={t('common.select_dots')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {ELEVENLABS_REGIONS.map((region) => (
                        <SelectItem key={region} value={region}>
                          {region}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      </Form>
    </FormDialog>
  );
};
